import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { usePaymentMode } from '../contexts/PaymentModeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Car, ChevronLeft, MapPin, Star, Calendar as CalendarIcon,
  ChevronRight, LayoutDashboard, Users, MessageCircle,
  AlertTriangle, AlertCircle, ShieldCheck, BadgeCheck, ImageIcon,
  Fuel, Settings, User, Camera, CreditCard, Scale, ClipboardCheck,
  Share2, Play, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import { getVehiclePhotoUrl, filterValidPhotos, handleVehicleImageError, getProfilePhotoUrl } from '../lib/imageUtils';
import HostModal from '../components/vehicles/HostModal';
import VerificationGateModal from '../components/vehicles/VerificationGateModal';
import LegalReacceptanceModal from '../components/LegalReacceptanceModal';
import { TripProtectionBadges } from '../components/TrustBadge';
import VehicleRulesCard, { TripRulesSummary } from '../components/VehicleRulesCard';
import SpecialtyRulesCard, { SpecialtyRulesSummary } from '../components/SpecialtyRulesCard';
import PickupLocationMap from '../components/PickupLocationMap';
import CollapsibleText from '../components/CollapsibleText';
import VehicleReviews, { VehicleRatingBadge } from '../components/VehicleReviews';
import { SendToFriendSection, SendToFriendModal } from '../components/SendToFriend';
import GuestCheckoutModal from '../components/GuestCheckoutModal';

// PHASE 2 (P0/P1): Category-specific operator declarations.
// Strictly additive — only rendered for vehicle categories listed below.
// Standard car and specialty asset bookings are unaffected. Backend mirror:
// `routes/bookings.py::CATEGORY_DECLARATIONS_REQUIRED`.
const CATEGORY_DECLARATIONS = {
  motorcycle: [
    { key: 'motorcycle_licence', label: 'I hold a valid motorcycle licence appropriate for this vehicle' },
  ],
  boat: [
    { key: 'marine_licence', label: 'I hold a valid marine/boat licence where required' },
  ],
  jetski: [
    { key: 'pwc_licence', label: 'I hold a valid Personal Watercraft (jet ski) licence' },
  ],
  trailer: [
    { key: 'tow_capability', label: 'My vehicle is capable of safely towing this trailer' },
  ],
  ebike: [
    { key: 'safe_operation', label: 'I will operate this eBike safely and in accordance with local laws' },
  ],
  electric_scooter: [
    { key: 'scooter_rules', label: 'I understand and will follow local e-scooter laws and restrictions' },
  ],
  mobility_scooter: [
    { key: 'safe_use', label: 'I am able to safely operate this mobility scooter' },
  ],
};

// Rego Warning Banner Component
const RegoWarningBanner = ({ regoStatus, isHostView = false }) => {
  if (!regoStatus || !regoStatus.warning) return null;
  
  const bannerStyles = {
    expired: {
      bg: 'bg-red-50 border-red-200',
      text: 'text-red-800',
      icon: <AlertCircle className="w-5 h-5 text-red-600" />
    },
    red: {
      bg: 'bg-red-50 border-red-200',
      text: 'text-red-800',
      icon: <AlertTriangle className="w-5 h-5 text-red-600" />
    },
    yellow: {
      bg: 'bg-yellow-50 border-yellow-200',
      text: 'text-yellow-800',
      icon: <AlertTriangle className="w-5 h-5 text-yellow-600" />
    }
  };
  
  const style = bannerStyles[regoStatus.warning_level] || bannerStyles.yellow;
  
  return (
    <div className={`${style.bg} border rounded-xl p-4 mb-4`} data-testid="rego-warning-banner">
      <div className="flex items-start gap-3">
        {style.icon}
        <div className="flex-1">
          <p className={`font-medium ${style.text}`}>
            {regoStatus.status === 'expired' ? 'Registration Expired' : 'Registration Expiring Soon'}
          </p>
          <p className={`text-sm ${style.text} opacity-80`}>
            {regoStatus.warning}
            {regoStatus.status === 'expired' && !isHostView && ' - This vehicle cannot be booked.'}
            {regoStatus.status === 'expired' && isHostView && ' - Please renew to make this vehicle visible to renters.'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Premium Media Gallery Component (images + optional video)
const PhotoGallery = ({ photos, videoKey, posterKey, vehicleName, vehicleId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [videoState, setVideoState] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'

  const validPhotos = filterValidPhotos(photos);

  // Build unified media list. Video slides in after the first image
  // (recommended placement) or becomes the first item if there are no photos.
  const media = React.useMemo(() => {
    const items = (validPhotos || []).map((p) => ({ type: 'image', src: getVehiclePhotoUrl(p), key: p }));
    if (videoKey) {
      const videoItem = {
        type: 'video',
        src: getVehiclePhotoUrl(videoKey),
        key: videoKey,
        poster: posterKey ? getVehiclePhotoUrl(posterKey) : null,
      };
      if (items.length === 0) {
        items.push(videoItem);
      } else {
        items.splice(1, 0, videoItem);
      }
    }
    return items;
  }, [validPhotos, videoKey, posterKey]);

  const mediaCount = media.length;

  // Clamp index if media list shrinks (e.g. video deleted)
  useEffect(() => {
    if (currentIndex >= mediaCount && mediaCount > 0) {
      setCurrentIndex(0);
    }
  }, [mediaCount, currentIndex]);

  // Swipe threshold
  const minSwipeDistance = 50;
  
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < mediaCount - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  
  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => prev === 0 ? Math.max(mediaCount - 1, 0) : prev - 1);
  }, [mediaCount]);
  
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => prev === mediaCount - 1 ? 0 : prev + 1);
  }, [mediaCount]);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext]);

  const activeItem = mediaCount > 0 ? media[Math.min(currentIndex, mediaCount - 1)] : null;
  
  return (
    <div className="rounded-2xl overflow-hidden bg-slate-100" data-testid="photo-gallery">
      {/* Main media slot */}
      <div 
        className="aspect-[16/10] sm:aspect-[16/9] relative"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {activeItem ? (
          activeItem.type === 'video' ? (
            <div className="relative w-full h-full bg-black" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
              <video
                key={activeItem.key}
                src={activeItem.src}
                poster={activeItem.poster || undefined}
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-contain bg-black"
                data-testid="gallery-video-slide"
                onLoadStart={() => setVideoState('loading')}
                onLoadedMetadata={() => setVideoState('ready')}
                onCanPlay={() => setVideoState('ready')}
                onError={() => setVideoState('error')}
              />
              {videoState === 'loading' && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-sm pointer-events-none" data-testid="gallery-video-loading">
                  Loading video…
                </div>
              )}
              {videoState === 'error' && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-sm pointer-events-none" data-testid="gallery-video-error">
                  Video failed to load
                </div>
              )}
            </div>
          ) : (
            <img 
              src={activeItem.src}
              alt={`${vehicleName} - Photo ${currentIndex + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => handleVehicleImageError(e, vehicleId)}
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <Car className="w-20 h-20 text-slate-300" />
          </div>
        )}
        
        {/* Navigation Arrows - Only show if multiple media items */}
        {mediaCount > 1 && (
          <>
            <button 
              onClick={goToPrevious}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-slate-700 shadow-lg transition-all hover:scale-105 z-10"
              aria-label="Previous media"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={goToNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-slate-700 shadow-lg transition-all hover:scale-105 z-10"
              aria-label="Next media"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
        
        {/* Counter Badge */}
        {mediaCount > 1 && (
          <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-black/60 backdrop-blur-sm text-white text-sm font-medium rounded-lg z-10">
            <ImageIcon className="w-4 h-4" />
            <span>{currentIndex + 1} / {mediaCount}</span>
          </div>
        )}
        
        {/* Dot Indicators */}
        {mediaCount > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {media.map((m, index) => (
              <button
                key={`dot-${index}`}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all ${
                  currentIndex === index 
                    ? 'w-6 bg-white' 
                    : 'w-2 bg-white/50 hover:bg-white/70'
                }`}
                aria-label={m.type === 'video' ? 'Go to video' : `Go to photo ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Thumbnail Strip - Desktop only */}
      {mediaCount > 1 && (
        <div className="hidden sm:flex p-3 gap-2 overflow-x-auto bg-white">
          {media.map((m, index) => (
            <button
              key={`thumb-${index}`}
              onClick={() => setCurrentIndex(index)}
              className={`relative w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                currentIndex === index 
                  ? 'border-[#004B8D] ring-2 ring-[#004B8D]/20' 
                  : 'border-transparent hover:border-slate-300'
              }`}
              data-testid={m.type === 'video' ? 'gallery-thumb-video' : `gallery-thumb-image-${index}`}
            >
              {m.type === 'video' ? (
                <>
                  {m.poster ? (
                    <img
                      src={m.poster}
                      alt=""
                      className="w-full h-full object-cover"
                      data-testid="gallery-thumb-video-poster"
                    />
                  ) : (
                    <video
                      src={m.src}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover bg-black pointer-events-none"
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-3.5 h-3.5 text-slate-900 ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                </>
              ) : (
                <img 
                  src={m.src}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Premium Host Section Component
const HostSection = ({ host, onMessage }) => {
  if (!host) return null;
  
  const photoUrl = getProfilePhotoUrl(host.profile_photo);
  const isVerified = host.verification_status === 'verified';
  
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="host-section">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {photoUrl ? (
            <img 
              src={photoUrl}
              alt={host.full_name || 'Host'}
              className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-md"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling?.classList?.remove('hidden');
              }}
            />
          ) : null}
          <div className={`w-14 h-14 rounded-full bg-gradient-to-br from-[#004B8D] to-[#00A3BF] flex items-center justify-center text-white text-lg font-semibold ${photoUrl ? 'hidden' : ''}`}>
            {host.full_name?.charAt(0)?.toUpperCase() || <User className="w-6 h-6" />}
          </div>
        </div>
        
        {/* Host Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-semibold text-slate-900">
              Hosted by {host.full_name?.split(' ')[0] || 'Host'}
            </h3>
            {isVerified && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
                <BadgeCheck className="w-3 h-3" />
                Verified
              </span>
            )}
          </div>
          
          {host.bio && (
            <p className="text-sm text-slate-500 line-clamp-2 mb-3">
              {host.bio}
            </p>
          )}
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={onMessage}
            className="h-9 rounded-lg text-sm"
            data-testid="message-host-btn"
          >
            <MessageCircle className="w-4 h-4 mr-1.5" />
            Message Host
          </Button>
        </div>
      </div>
    </div>
  );
};

// Vehicle Specs Component
const VehicleSpecs = ({ vehicle }) => {
  const specs = [];
  const cs = vehicle.category_specs || {};
  
  const CATEGORY_LABELS = {
    car: 'Car',
    motorcycle: 'Motorcycle',
    boat: 'Boat',
    jetski: 'Jet Ski',
    trailer: 'Trailer',
    ebike: 'eBike',
    electric_scooter: 'Electric Scooter',
    mobility_scooter: 'Mobility Scooter',
    specialty_asset: 'Specialty Asset'
  };
  
  const categoryLabel = vehicle.vehicle_category 
    ? (CATEGORY_LABELS[vehicle.vehicle_category] || vehicle.vehicle_category)
    : null;
  
  const isSpecialty = vehicle.vehicle_category === 'specialty_asset';
  
  if (vehicle.vehicle_category && vehicle.vehicle_category !== 'car' && categoryLabel) {
    specs.push({ icon: Car, label: categoryLabel, capitalize: true });
  } else if (vehicle.vehicle_type) {
    specs.push({ icon: Car, label: vehicle.vehicle_type, capitalize: true });
  }
  
  // Car specs — hidden for specialty assets (no driving / fuel / seats context)
  if (!isSpecialty && (cs.transmission || vehicle.transmission)) specs.push({ icon: Settings, label: cs.transmission || vehicle.transmission });
  if (!isSpecialty && (cs.fuel_type || vehicle.fuel_type)) specs.push({ icon: Fuel, label: cs.fuel_type || vehicle.fuel_type });
  if (!isSpecialty && (cs.seats || vehicle.seats)) specs.push({ icon: Users, label: `${cs.seats || vehicle.seats} seats` });
  
  // Motorcycle
  if (cs.motorcycle_type) specs.push({ icon: Settings, label: cs.motorcycle_type, capitalize: true });
  if (cs.engine_cc) specs.push({ icon: Settings, label: `${cs.engine_cc}cc` });
  if (cs.licence_class_required) specs.push({ icon: ShieldCheck, label: `Licence: ${cs.licence_class_required}` });
  
  // Boat
  if (cs.boat_type) specs.push({ icon: Settings, label: cs.boat_type.replace('_', ' '), capitalize: true });
  if (cs.passenger_capacity) specs.push({ icon: Users, label: `${cs.passenger_capacity} passengers` });
  if (cs.hull_length) specs.push({ icon: Settings, label: `${cs.hull_length}m hull` });
  if (cs.boat_licence_required) specs.push({ icon: ShieldCheck, label: 'Boat licence required' });
  
  // Jet Ski
  if (cs.rider_capacity && vehicle.vehicle_category === 'jetski') specs.push({ icon: Users, label: `${cs.rider_capacity} riders` });
  if (cs.licence_required && vehicle.vehicle_category === 'jetski') specs.push({ icon: ShieldCheck, label: 'Licence required' });
  
  // Trailer
  if (cs.trailer_type) specs.push({ icon: Settings, label: cs.trailer_type.replace('_', ' '), capitalize: true });
  if (cs.load_capacity) specs.push({ icon: Settings, label: `${cs.load_capacity}kg capacity` });
  if (cs.braked_or_unbraked) specs.push({ icon: Settings, label: cs.braked_or_unbraked, capitalize: true });
  
  // eBike / Electric Scooter
  if (cs.battery_range_km && (vehicle.vehicle_category === 'ebike' || vehicle.vehicle_category === 'electric_scooter')) {
    specs.push({ icon: Fuel, label: `${cs.battery_range_km}km range` });
  }
  if (cs.top_speed_kmh && (vehicle.vehicle_category === 'ebike' || vehicle.vehicle_category === 'electric_scooter')) {
    specs.push({ icon: Settings, label: `${cs.top_speed_kmh}km/h` });
  }
  if (cs.rider_capacity && (vehicle.vehicle_category === 'ebike' || vehicle.vehicle_category === 'electric_scooter')) {
    specs.push({ icon: Users, label: `${cs.rider_capacity} rider${cs.rider_capacity > 1 ? 's' : ''}` });
  }
  
  // Mobility Scooter
  if (cs.battery_range_km && vehicle.vehicle_category === 'mobility_scooter') {
    specs.push({ icon: Fuel, label: `${cs.battery_range_km}km range` });
  }
  if (cs.top_speed_kmh && vehicle.vehicle_category === 'mobility_scooter') {
    specs.push({ icon: Settings, label: `${cs.top_speed_kmh}km/h max` });
  }
  if (cs.weight_capacity_kg) specs.push({ icon: Settings, label: `${cs.weight_capacity_kg}kg capacity` });
  if (cs.foldable) specs.push({ icon: Settings, label: 'Foldable' });
  if (cs.transport_friendly) specs.push({ icon: ShieldCheck, label: 'Transport Friendly' });
  
  if (specs.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-3" data-testid="vehicle-specs">
      {specs.map((spec, index) => (
        <div 
          key={index}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 text-sm rounded-lg"
        >
          <spec.icon className="w-4 h-4 text-slate-500" />
          <span className={spec.capitalize ? 'capitalize' : ''}>{spec.label}</span>
        </div>
      ))}
    </div>
  );
};

// HireHub Protection Trust Panel Component
const HireHubProtection = ({ category }) => {
  const { isHostDirect } = usePaymentMode();
  const isSpecialty = category === 'specialty_asset';
  const hireNoun = isSpecialty ? 'hire' : 'trip';
  const hireNounCap = isSpecialty ? 'Hire' : 'Trip';
  const protectionItems = [
    {
      icon: Camera,
      title: 'Evidence-backed rentals',
      description: `Every ${hireNoun} requires photo inspections before and after.`,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      icon: CreditCard,
      title: isHostDirect ? 'Direct payment mode' : 'Secure payments',
      description: isHostDirect
        ? 'Payment is arranged directly with the host.'
        : 'Payments processed securely.',
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      icon: Scale,
      title: 'Admin dispute review',
      description: 'Damage claims reviewed with photo evidence.',
      color: 'text-amber-600',
      bg: 'bg-amber-50'
    },
    {
      icon: ClipboardCheck,
      title: `${hireNounCap} inspection required`,
      description: `Pre-${hireNoun} and post-${hireNoun} inspections protect both sides.`,
      color: 'text-cyan-600',
      bg: 'bg-cyan-50'
    }
  ];

  return (
    <div className="bg-slate-50 rounded-2xl p-5" data-testid="hirehub-protection">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5 text-[#004B8D]" />
        <h3 className="font-semibold text-slate-900">HireHub Protection</h3>
      </div>
      
      <div className="space-y-3">
        {protectionItems.map((item, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-lg ${item.bg} flex items-center justify-center flex-shrink-0`}>
              <item.icon className={`w-4 h-4 ${item.color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800">{item.title}</p>
              <p className="text-xs text-slate-500 leading-relaxed">{item.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Booking Card Component
const BookingCard = ({ 
  vehicle, 
  selectedRange, 
  onDateRangeSelect,
  pricing,
  hostFeeMessage,
  isDateBlocked, 
  onRequestToBook,
  isExpired,
  bookingCount,
  hasShownAvailabilityNudge,
  onShowAvailabilityNudge
}) => {
  const { isHostDirect } = usePaymentMode();
  // Safe check for valid date range
  const hasValidRange = selectedRange?.from && selectedRange?.to;
  
  // Show availability nudge when dates are selected (once per session)
  const showNudge = hasValidRange && !hasShownAvailabilityNudge;
  
  // Trigger nudge shown callback
  React.useEffect(() => {
    if (showNudge && onShowAvailabilityNudge) {
      onShowAvailabilityNudge();
    }
  }, [showNudge, onShowAvailabilityNudge]);
  
  return (
    <Card className="border-0 shadow-lg rounded-2xl overflow-hidden" data-testid="booking-card">
      <CardContent className="p-5 sm:p-6">
        {/* Price Header */}
        <div className="flex items-baseline gap-1.5 mb-2 pb-3 border-b border-slate-100">
          <span className="text-3xl font-bold text-slate-900">${vehicle.daily_rate}</span>
          <span className="text-slate-500 text-base">/day</span>
        </div>

        {vehicle.vehicle_category === 'specialty_asset' && (
          <p className="text-sm text-amber-700 -mt-1 mb-3" data-testid="specialty-asset-trust-line">
            Perfect for private events, parties, and functions.
          </p>
        )}
        
        {/* Host Trust Row - compact */}
        {vehicle.owner_details && (
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 pb-3 border-b border-slate-100">
            {vehicle.owner_details.verification_status === 'verified' && (
              <span className="flex items-center gap-1 text-emerald-600">
                <BadgeCheck className="w-3.5 h-3.5" />
                Verified
              </span>
            )}
            {vehicle.completed_trips > 0 && (
              <>
                <span className="text-slate-300">•</span>
                <span>{vehicle.completed_trips} {vehicle.vehicle_category === 'specialty_asset' ? 'hires' : 'trips'}</span>
              </>
            )}
            <span className="text-slate-300">•</span>
            <span>Hosted by {vehicle.owner_details.full_name?.split(' ')[0] || 'Host'}</span>
          </div>
        )}
        
        {/* Social Proof: Recent Bookings */}
        <p className="text-xs text-slate-500 mb-4 flex items-center gap-1" data-testid="booking-count-badge">
          🔥 Booked {bookingCount} times this week
        </p>
        
        {/* Date Selector */}
        <div className="mb-4">
          <p className="text-sm font-medium text-slate-700 mb-3">Select your dates</p>
          <Calendar
            mode="range"
            selected={selectedRange?.from ? selectedRange : undefined}
            onSelect={onDateRangeSelect}
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const compareDate = new Date(date);
              compareDate.setHours(0, 0, 0, 0);
              return compareDate < today || isDateBlocked(date);
            }}
            className="rounded-xl border border-slate-200 w-full"
          />
          {/* Availability Nudge - shown once when dates selected */}
          {showNudge && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1" data-testid="availability-nudge">
              ⚠️ Limited availability for your dates
            </p>
          )}
        </div>
        
        {/* Pricing Breakdown - 3 Sections */}
        {pricing && (
          <div className="mb-5 space-y-3">
            {/* SECTION 1: Trip Cost */}
            <div className="p-4 bg-slate-50 rounded-xl space-y-2.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{vehicle.vehicle_category === 'specialty_asset' ? 'Hire Cost' : 'Trip Cost'}</p>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">${vehicle.daily_rate} × {pricing.totalDays} days</span>
                <span className="text-slate-900">${pricing.subtotal.toFixed(2)}</span>
              </div>
              {isHostDirect ? (
                <p
                  className="text-xs italic text-slate-500"
                  data-testid="no-platform-fee-note-sidebar"
                >
                  No platform fee (direct payment mode)
                </p>
              ) : (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Service fee</span>
                    <span className="text-slate-600">${pricing.platformFee.toFixed(2)}</span>
                  </div>
                  {hostFeeMessage && (
                    <p className="text-xs text-emerald-600/80 mt-1" data-testid="host-fee-benefit-sidebar">{hostFeeMessage}</p>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Card processing fee</span>
                    <span className="text-slate-600">${pricing.cardFee.toFixed(2)}</span>
                  </div>
                </>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold text-slate-900">
                <span>{vehicle.vehicle_category === 'specialty_asset' ? 'Hire Total' : 'Trip Total'}</span>
                <span>${(isHostDirect ? pricing.subtotal : pricing.tripTotal).toFixed(2)}</span>
              </div>
            </div>
            
            {/* SECTION 2: Security Bond */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {isHostDirect ? 'Security Bond' : 'Security Bond (Temporary Hold)'}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    {isHostDirect
                      ? (vehicle.vehicle_category === 'specialty_asset'
                          ? 'Collected directly by the host and recorded in HireHub for transparency. The host will confirm the bond amount and collection method before the hire begins. HireHub does not place a card hold in direct payment mode.'
                          : 'Collected directly by the host and recorded in HireHub for transparency. The host will confirm the bond amount and collection method before the trip begins. HireHub does not place a card hold in direct payment mode.')
                      : (vehicle.vehicle_category === 'specialty_asset'
                          ? 'This is not charged. It is a temporary hold released after the hire.'
                          : 'This is not charged. It is a temporary hold released after the trip.')}
                  </p>
                </div>
                <span className="text-sm font-semibold text-amber-800">${pricing.bondAmount.toFixed(2)}</span>
              </div>
            </div>
            
            {/* SECTION 3: Total Charged Today (platform mode) / Pay to Host (host_direct) */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex justify-between font-bold">
                <span className="text-blue-900">
                  {isHostDirect ? 'Pay to Host' : 'Total Charged Today'}
                </span>
                <span className="text-lg text-blue-900">
                  ${(isHostDirect ? pricing.subtotal : pricing.tripTotal).toFixed(2)} AUD
                </span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                {isHostDirect
                  ? `Arrange payment directly with the host. Security bond of $${pricing.bondAmount.toFixed(0)} is handled separately.`
                  : `Does not include the $${pricing.bondAmount.toFixed(0)} security bond hold`}
              </p>
            </div>
          </div>
        )}
        
        {/* Rego Expired Warning */}
        {isExpired && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl mb-4">
            <p className="text-sm text-red-700 font-medium flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4" />
              Vehicle unavailable - Registration expired
            </p>
          </div>
        )}
        
        {/* CTA Button */}
        <Button 
          onClick={onRequestToBook}
          className="w-full bg-[#004B8D] hover:bg-[#003a6d] h-12 rounded-xl text-base font-medium shadow-sm"
          disabled={!hasValidRange || isExpired}
          data-testid="request-booking-btn"
        >
          {isExpired
            ? 'Unavailable'
            : vehicle.vehicle_category === 'specialty_asset'
              ? 'Request Booking (Event Hire)'
              : 'Request to Book — No charge yet'}
        </Button>
        
        {/* CTA Microcopy - reassurance block */}
        <div className="mt-3 space-y-1.5 text-center">
          <p className="text-xs text-slate-500">
            {!hasValidRange ? 'Select dates above to see pricing' : 'You won\'t be charged until the host accepts'}
          </p>
          <p className="text-xs text-slate-400">
            ID verification required before booking
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const VehicleDetails = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { user, api, isVerified, refreshLegalCompliance, needsLegalReacceptance, isPlatformOperator, refreshUser } = useAuth();
  const { isHostDirect } = usePaymentMode();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState([]);
  
  // Initialize date range from sessionStorage (preserves dates across verification flow)
  const [selectedRange, setSelectedRange] = useState(() => {
    const savedStart = sessionStorage.getItem('bookingStartDate');
    const savedEnd = sessionStorage.getItem('bookingEndDate');
    const savedVehicleId = sessionStorage.getItem('bookingVehicleId');
    
    // Only restore if it's for the same vehicle (prevents date confusion)
    if (savedStart && savedEnd && savedVehicleId === vehicleId) {
      const startDate = new Date(savedStart);
      const endDate = new Date(savedEnd);
      // Validate dates are in the future
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate >= today && endDate >= startDate) {
        return { from: startDate, to: endDate };
      }
    }
    return { from: null, to: null };
  });
  
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [acceptedRules, setAcceptedRules] = useState(false);
  // Specialty-asset-only confirmation that the renter has read the
  // "what you must supply" section. Frontend-only gating — never sent in any
  // payload and never persisted. Mirrors acceptedRules state pattern.
  const [confirmedSupplies, setConfirmedSupplies] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showMobileCalendar, setShowMobileCalendar] = useState(false);
  const [showHostModal, setShowHostModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [showGuestCheckoutModal, setShowGuestCheckoutModal] = useState(false);
  // Share modal state for header share button
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinks, setShareLinks] = useState(null);
  // P0 LICENCE VALIDITY: Licence declaration state
  const [licenceDeclaration, setLicenceDeclaration] = useState({
    declared_licence_valid: false,
    declared_not_suspended: false,
    declared_legally_permitted: false,
    declared_licence_belongs_to_me: false
  });
  const [licenceValidity, setLicenceValidity] = useState(null);
  // PHASE 2 (P0/P1): Category-specific operator declarations.
  // Only rendered when the listing's vehicle_category appears in this map.
  // Strictly additive — does not affect cars or specialty assets.
  const [categoryDeclarations, setCategoryDeclarations] = useState({});

  // Live platform fee config (replaces hardcoded 15%)
  const [pricingConfig, setPricingConfig] = useState(null);

  // Conversion enhancement: Social proof placeholders (static for now)
  const [viewCount] = useState(() => Math.floor(Math.random() * 14) + 5); // 5-18 views
  const [bookingCount] = useState(() => Math.floor(Math.random() * 4) + 2); // 2-5 bookings
  const [hasShownAvailabilityNudge, setHasShownAvailabilityNudge] = useState(false);

  // P3: Platform operators are exempt from legal acceptance checks
  const userNeedsLegalAcceptance = user && !isPlatformOperator() && needsLegalReacceptance && needsLegalReacceptance();

  // P0 FIX: Track whether user context is fresh to prevent stale-state booking gate
  const [userRefreshed, setUserRefreshed] = useState(!user);

  useEffect(() => {
    fetchVehicle();
    fetchAvailability();
    trackVehicleView();
    // Fetch live platform fee config
    api.get('/vehicles/pricing-config').then(r => setPricingConfig(r.data)).catch(() => {});
    // P0 GUEST CHECKOUT: Refresh user data when returning from verification
    // This ensures verification_status is current (e.g. after uploading docs)
    if (user && refreshUser) {
      setUserRefreshed(false);
      refreshUser().then(() => setUserRefreshed(true)).catch(() => setUserRefreshed(true));
    }
    // P0 LICENCE VALIDITY: Check licence validity when user is logged in
    if (user) {
      checkLicenceValidity();
    }
  }, [vehicleId]);

  // P0 LICENCE VALIDITY: Check if user's licence is valid for booking
  const checkLicenceValidity = async () => {
    try {
      const response = await api.get('/auth/me/licence-validity');
      setLicenceValidity(response.data);
    } catch (error) {
      setLicenceValidity(null);
    }
  };

  // Fetch share links for the header share button
  const fetchShareLinks = async () => {
    try {
      const response = await api.get(`/vehicles/${vehicleId}/share-links`);
      setShareLinks(response.data);
      setShowShareModal(true);
    } catch (error) {
      toast.error('Failed to load share options');
    }
  };

  const handleHeaderShare = () => {
    if (shareLinks) {
      setShowShareModal(true);
    } else {
      fetchShareLinks();
    }
  };

  const trackVehicleView = async () => {
    try {
      // Track view for analytics (fire and forget)
      await api.post(`/vehicles/${vehicleId}/track-view`);
    } catch (error) {
      // Silently fail - don't disrupt user experience
    }
  };

  const fetchVehicle = async () => {
    try {
      const response = await api.get(`/vehicles/${vehicleId}`);
      setVehicle(response.data);
    } catch (error) {
      toast.error('Vehicle not found');
      navigate('/vehicles');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await api.get(`/vehicles/${vehicleId}/availability`);
      setAvailability(response.data.availability || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const blockedDates = availability
    .filter(a => !a.is_available)
    .map(a => new Date(a.date));

  const isDateBlocked = (date) => {
    return blockedDates.some(blocked => 
      format(blocked, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
  };

  // Safe date range handler to prevent crashes from undefined values
  const handleDateRangeSelect = useCallback((range) => {
    // If range is undefined/null (e.g., clicking same date twice), reset to empty
    if (!range) {
      setSelectedRange({ from: null, to: null });
      // Clear sessionStorage
      sessionStorage.removeItem('bookingStartDate');
      sessionStorage.removeItem('bookingEndDate');
      sessionStorage.removeItem('bookingVehicleId');
      return;
    }
    // Ensure both from and to are either valid dates or null
    const newRange = {
      from: range.from || null,
      to: range.to || null
    };
    setSelectedRange(newRange);
    
    // Persist to sessionStorage for verification flow preservation
    if (newRange.from && newRange.to) {
      sessionStorage.setItem('bookingStartDate', newRange.from.toISOString());
      sessionStorage.setItem('bookingEndDate', newRange.to.toISOString());
      sessionStorage.setItem('bookingVehicleId', vehicleId);
    }
  }, [vehicleId]);

  const calculatePricing = useCallback(() => {
    // Safe checks - ensure selectedRange exists and has valid from/to dates
    if (!selectedRange || !selectedRange.from || !selectedRange.to || !vehicle) return null;
    
    // Additional validation to ensure dates are valid Date objects
    if (!(selectedRange.from instanceof Date) || !(selectedRange.to instanceof Date)) return null;
    
    const totalDays = differenceInDays(selectedRange.to, selectedRange.from) + 1;
    if (totalDays < 1) return null; // Invalid date range
    
    const subtotal = vehicle.daily_rate * totalDays;
    const feePercent = pricingConfig ? pricingConfig.renter_fee_percent / 100 : 0.119;
    const platformFee = subtotal * feePercent;
    const processingPercent = pricingConfig?.processing_fee_percent ?? 0.025;
    const processingFixed = pricingConfig?.processing_fee_fixed ?? 0.50;
    const cardFee = Math.round(((subtotal + platformFee) * processingPercent + processingFixed) * 100) / 100;
    const bondAmount = vehicle.bond_amount || vehicle.owner_rules?.security_bond || 500;
    const tripTotal = subtotal + platformFee + cardFee;
    const totalAmount = tripTotal + bondAmount;

    return { totalDays, subtotal, platformFee, cardFee, bondAmount, tripTotal, totalAmount };
  }, [selectedRange, vehicle, pricingConfig]);

  // GUEST CHECKOUT FLOW: Check if user is a provisional guest checkout user
  // FIXED: Use created_via as primary check (doesn't change after password set)
  // password_reset_required becomes false after they set their password, but created_via remains 'guest_checkout'
  const isGuestCheckoutUser = user?.created_via === 'guest_checkout';

  // Check if user can proceed with booking
  // For guest checkout users: allow 'pending' verification status (docs uploaded, awaiting review)
  // For normal users: require 'verified' status
  const canBook = () => {
    if (!user) return false;
    
    // Normal users must be fully verified
    if (!isGuestCheckoutUser) {
      return user.verification_status === 'verified';
    }
    
    // Guest checkout users can proceed with 'pending' or 'verified' status
    // This allows them to submit booking request after uploading docs
    return user.verification_status === 'verified' || user.verification_status === 'pending';
  };

  // Check if guest checkout user has uploaded required docs (pending review)
  const isGuestWithPendingVerification = isGuestCheckoutUser && user?.verification_status === 'pending';

  // Helper function for safe date display
  const hasValidDateRange = useCallback(() => {
    return selectedRange && selectedRange.from && selectedRange.to;
  }, [selectedRange]);

  const hasStartDate = useCallback(() => {
    return selectedRange && selectedRange.from;
  }, [selectedRange]);

  const handleRequestToBook = () => {
    // GUEST CHECKOUT: Show guest checkout modal for non-logged-in users
    if (!user) {
      setShowGuestCheckoutModal(true);
      return;
    }

    // P0 FIX: Don't block guest checkout users who already have 'pending' or 'verified' status.
    // Their data is already current from VerificationPage's refreshUser() call.
    // Only block on userRefreshed for users whose booking eligibility is uncertain.
    const guestWithSufficientData = isGuestCheckoutUser && 
      (user.verification_status === 'pending' || user.verification_status === 'verified');

    if (!userRefreshed && !guestWithSufficientData) {
      toast.info('Loading your account details...');
      return;
    }

    if (!canBook()) {
      setShowVerificationModal(true);
      return;
    }

    if (!hasValidDateRange()) {
      toast.error('Please select booking dates');
      return;
    }

    setShowBookingModal(true);
  };

  const handleBookingRequest = async () => {
    // GUEST CHECKOUT: Show guest checkout modal for non-logged-in users
    if (!user) {
      setShowGuestCheckoutModal(true);
      return;
    }

    // GUEST CHECKOUT FLOW: Allow pending verification for guest checkout users
    // Normal users must be fully verified; guest checkout users can proceed with pending
    if (!isGuestCheckoutUser && !isVerified()) {
      setShowVerificationModal(true);
      return;
    }
    
    // Guest checkout users must have at least uploaded docs (pending status)
    if (isGuestCheckoutUser && user.verification_status === 'unverified') {
      setShowVerificationModal(true);
      return;
    }

    // P0 LICENCE VALIDITY: Check if re-verification is required
    if (licenceValidity?.requires_reverification) {
      toast.error('Licence re-verification required. Please update your verification documents.');
      navigate('/verification');
      return;
    }

    if (!acceptedRules) {
      toast.error('Please accept the host rules to continue');
      return;
    }

    // Specialty-asset only: require explicit confirmation of supply
    // responsibilities when the host has declared what the renter must
    // bring. UI-only gate; nothing extra is added to the booking payload.
    if (
      vehicle?.vehicle_category === 'specialty_asset' &&
      vehicle?.category_specs?.renter_must_supply &&
      !confirmedSupplies
    ) {
      toast.error('Please confirm you understand your supply responsibilities');
      return;
    }

    // P0 LICENCE VALIDITY: Validate all licence declarations
    const allDeclarationsAccepted = 
      licenceDeclaration.declared_licence_valid &&
      licenceDeclaration.declared_not_suspended &&
      licenceDeclaration.declared_legally_permitted &&
      licenceDeclaration.declared_licence_belongs_to_me;
    
    if (!allDeclarationsAccepted) {
      toast.error('Please confirm all licence validity declarations');
      return;
    }

    // PHASE 2 (P0/P1): Block submit when any category-specific declaration
    // is unchecked. Only applies to categories listed in CATEGORY_DECLARATIONS.
    const requiredCategoryDecls = CATEGORY_DECLARATIONS[vehicle?.vehicle_category] || [];
    if (requiredCategoryDecls.length > 0) {
      const missingCategoryDecls = requiredCategoryDecls.filter((d) => !categoryDeclarations[d.key]);
      if (missingCategoryDecls.length > 0) {
        toast.error('Please confirm all required operator responsibilities');
        return;
      }
    }

    const pricing = calculatePricing();
    if (!pricing) return;

    setBookingLoading(true);
    try {
      const response = await api.post('/bookings', {
        vehicle_id: vehicleId,
        start_date: format(selectedRange.from, 'yyyy-MM-dd'),
        end_date: format(selectedRange.to, 'yyyy-MM-dd'),
        // P0 LICENCE VALIDITY: Include licence declaration
        licence_declaration: {
          declaration_version: 'v1.0',
          ...licenceDeclaration
        },
        // Specialty asset only — supply responsibility audit log (when host
        // declared category_specs.renter_must_supply). Includes the exact
        // text the renter saw at booking time so it can be referenced in
        // disputes. Backend persists this to booking.supply_confirmation.
        ...(vehicle?.vehicle_category === 'specialty_asset' && vehicle?.category_specs?.renter_must_supply ? {
          supply_confirmation: {
            confirmed: confirmedSupplies,
            confirmed_text: vehicle.category_specs.renter_must_supply,
          }
        } : {}),
        // PHASE 2 (P0/P1): Category-specific operator declarations payload.
        // Only included for categories listed in CATEGORY_DECLARATIONS. Flat
        // dict of {key: bool} mirrored on the backend's `category_declarations`
        // store. Existing licence_declaration is unchanged.
        ...((CATEGORY_DECLARATIONS[vehicle?.vehicle_category] || []).length > 0 ? {
          category_declarations: (CATEGORY_DECLARATIONS[vehicle?.vehicle_category] || []).reduce(
            (acc, d) => ({ ...acc, [d.key]: !!categoryDeclarations[d.key] }),
            {}
          ),
        } : {}),
        // GUEST CHECKOUT: Flag for backend to know this is a guest checkout booking
        is_guest_checkout: isGuestCheckoutUser
      });
      
      // GUEST CHECKOUT: Redirect to dedicated success page with in-app password setup
      if (isGuestCheckoutUser) {
        setShowBookingModal(false);
        
        // Store booking context for "Return to booking" feature BEFORE clearing
        const bookingContext = {
          vehicleId: vehicleId,
          startDate: format(selectedRange.from, 'yyyy-MM-dd'),
          endDate: format(selectedRange.to, 'yyyy-MM-dd')
        };
        
        // Clear persisted booking dates after successful booking
        sessionStorage.removeItem('bookingStartDate');
        sessionStorage.removeItem('bookingEndDate');
        sessionStorage.removeItem('bookingVehicleId');
        
        // Navigate to guest success page with booking context including dates
        navigate('/guest-booking-success', {
          replace: true,
          state: {
            bookingId: response.data.booking_id,
            vehicleTitle: vehicle?.title || 'Vehicle Booking',
            vehicleId: vehicleId,
            startDate: bookingContext.startDate,
            endDate: bookingContext.endDate
          }
        });
        return;
      }
      
      // Normal users: Standard flow
      toast.success('Booking request submitted!');
      
      setShowBookingModal(false);
      
      // Clear persisted booking dates after successful booking
      sessionStorage.removeItem('bookingStartDate');
      sessionStorage.removeItem('bookingEndDate');
      sessionStorage.removeItem('bookingVehicleId');
      
      navigate(`/bookings/${response.data.booking_id}`);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      const status = error.response?.status;
      
      if (status === 403 && (detail.includes('Terms') || detail.includes('Privacy') || detail.includes('accept'))) {
        setShowLegalModal(true);
      } else if (status === 403 && detail.includes('re-verification')) {
        toast.error(detail);
        navigate('/verification');
      } else {
        toast.error(detail || 'Failed to submit booking request');
      }
    } finally {
      setBookingLoading(false);
    }
  };

  const handleLegalAccepted = async () => {
    setShowLegalModal(false);
    await refreshLegalCompliance();
    toast.success('Terms accepted! You can now complete your booking.');
  };

  const handleMessageHost = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/vehicles/${vehicleId}` } });
      return;
    }

    if (user.user_id === vehicle?.owner_id) {
      toast.error("You can't message yourself");
      return;
    }

    try {
      const response = await api.post('/messages/threads', {
        thread_type: 'inquiry',
        vehicle_id: vehicleId
      });
      
      navigate(`/messages/${response.data.thread_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open conversation');
    }
  };

  const rules = vehicle?.owner_rules || {};
  const pricing = calculatePricing();
  const isExpired = vehicle?.rego_status?.status === 'expired';

  // Host-driven pricing benefit display (read-only, no backend changes)
  // Shows subtle "lower fees" message only when host tier data is available on the vehicle
  const hostFeeMessage = useMemo(() => {
    if (!pricing) return null;
    const hostTier = vehicle?.owner_details?.subscription_tier;
    if (!hostTier || hostTier === 'free') return null;
    const tierLabel = { basic: 'Basic', pro: 'Pro', premium: 'Premium' }[hostTier];
    return tierLabel ? `This host's ${tierLabel} plan reduces your service fees` : null;
  }, [pricing, vehicle]);

  const getDashboardRoute = () => {
    if (!user) return '/';
    if (user.roles?.includes('platform_owner')) return '/platform-owner';
    if (user.roles?.includes('super_admin')) return '/super-admin';
    if (user.roles?.includes('admin')) return '/admin';
    if (user.roles?.includes('owner')) return '/host';
    return '/dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-[#004B8D]"></div>
      </div>
    );
  }

  if (!vehicle) return null;

  // Build share metadata - ensure all values are safe plain strings with fallbacks
  const vehicleMake = vehicle.make || '';
  const vehicleModel = vehicle.model || '';
  const vehicleName = (vehicleMake + ' ' + vehicleModel).trim() || 'Vehicle';
  const vehicleYear = vehicle.year ? String(vehicle.year) : '';
  const vehicleLocation = typeof vehicle.location === 'object' 
    ? `${vehicle.location.city || ''}${vehicle.location.city && vehicle.location.state ? ', ' : ''}${vehicle.location.state || ''}`
    : (vehicle.location || 'Australia');
  const vehicleRate = vehicle.daily_rate ? String(vehicle.daily_rate) : '0';
  const primaryPhoto = vehicle.photos && vehicle.photos.length > 0 ? getVehiclePhotoUrl(vehicle.photos[0]) : '';
  
  // Pre-compute meta tag values
  const ogDescription = vehicleYear 
    ? 'Check out this ' + vehicleYear + ' ' + vehicleName + ' available for $' + vehicleRate + '/day in ' + vehicleLocation + '!'
    : 'Check out this ' + vehicleName + ' available for $' + vehicleRate + '/day in ' + vehicleLocation + '!';

  return (
    <div className="min-h-screen bg-slate-50 pb-24 lg:pb-8">
      {/* P0 SHARE LISTING: Dynamic Open Graph meta tags for social sharing */}
      <Helmet
        title={vehicleName + ' for Hire - HireHub Australia'}
        meta={[
          { name: 'description', content: ogDescription },
          { property: 'og:title', content: vehicleName + ' for Hire on HireHub' },
          { property: 'og:description', content: ogDescription },
          { property: 'og:type', content: 'product' },
          { property: 'og:site_name', content: 'HireHub Australia' },
          ...(primaryPhoto ? [{ property: 'og:image', content: primaryPhoto }] : []),
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: vehicleName + ' for Hire on HireHub' },
          ...(primaryPhoto ? [{ name: 'twitter:image', content: primaryPhoto }] : [])
        ]}
      />
      
      {/* Legal Acceptance Warning Banner */}
      {userNeedsLegalAcceptance && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">Please accept our updated Terms & Privacy Policy to make bookings</span>
            </div>
            <Button 
              size="sm" 
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => setShowLegalModal(true)}
              data-testid="accept-terms-banner-btn"
            >
              Accept Now
            </Button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9 sm:h-10 sm:w-10">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Link to={user ? getDashboardRoute() : "/"} className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#004B8D] rounded-xl flex items-center justify-center">
                <Car className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="font-heading font-bold text-lg sm:text-xl text-[#004B8D] hidden xs:inline">HireHub</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {/* Share Button - Always visible */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleHeaderShare}
              className="h-9 gap-2 text-sm"
              data-testid="header-share-btn"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </Button>
            {user && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate(getDashboardRoute())}
                className="h-9 gap-2 text-sm"
                data-testid="goto-dashboard-btn"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-5 sm:py-6 lg:py-8">
        <div className="grid lg:grid-cols-3 gap-5 lg:gap-8">
          {/* Left Column - Main Content */}
          {/* Specialty assets: enforce `min-w-0` so the CSS-Grid column can shrink
              below the intrinsic width of its descendants (chip strips, rule
              rows, etc.) — fixes mobile right-side clipping on specialty_asset
              listings. Vehicle/other categories are unaffected. */}
          <div className={`lg:col-span-2 space-y-5 relative z-0 ${vehicle.vehicle_category === 'specialty_asset' ? 'min-w-0' : ''}`}>
            {/* Photo Gallery */}
            <PhotoGallery 
              photos={vehicle.photos} 
              videoKey={vehicle.listing_video_key || vehicle.listing_video_s3_key}
              posterKey={vehicle.listing_video_poster_key || vehicle.listing_video_poster_s3_key}
              vehicleName={`${vehicle.make} ${vehicle.model}`}
              vehicleId={vehicle.vehicle_id}
            />
            
            {/* Vehicle Title Block */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="vehicle-title-block">
              <RegoWarningBanner regoStatus={vehicle.rego_status} />
              
              {/* Title Row */}
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  {vehicle.vehicle_category === 'specialty_asset' && (
                    <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="specialty-asset-badge-block">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold" data-testid="specialty-asset-badge">
                        Specialty Asset
                      </span>
                      {vehicle.category_specs?.food_service_ready && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-md shadow-sm" data-testid="event-ready-badge">
                          Event Ready
                        </span>
                      )}
                      <p className="w-full text-xs text-amber-700 mt-1" data-testid="specialty-asset-tagline">
                        Best suited for events, private functions, and controlled use.
                      </p>
                    </div>
                  )}
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
                    {vehicle.make} {vehicle.model}
                  </h1>
                  <div className="flex items-center gap-2 text-slate-500">
                    <span>{vehicle.year}</span>
                    <span className="text-slate-300">•</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {typeof vehicle.location === 'object' 
                        ? `${vehicle.location.city || ''}${vehicle.location.city && vehicle.location.state ? ', ' : ''}${vehicle.location.state || ''}`
                        : vehicle.location}
                    </span>
                  </div>
                </div>
                {/* Rating Badge - Uses actual vehicle data */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 rounded-lg" data-testid="vehicle-rating-badge">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold text-slate-700">
                    {vehicle.avg_rating ? vehicle.avg_rating.toFixed(1) : '—'}
                  </span>
                  {vehicle.completed_trips > 0 && (
                    <>
                      <span className="text-slate-300 text-sm">·</span>
                      <span className="text-slate-500 text-sm">{vehicle.completed_trips} {vehicle.vehicle_category === 'specialty_asset' ? 'hires' : 'trips'}</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Social Proof: View Count */}
              <p className="text-xs text-slate-400 mb-3" data-testid="view-count-badge">
                👀 {viewCount} people viewed this today
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {vehicle.owner_details?.verification_status === 'verified' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
                    <BadgeCheck className="w-3.5 h-3.5" />
                    Verified Host
                  </span>
                )}
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {isHostDirect ? 'Direct Payment' : 'Secure Payments'}
                </span>
              </div>

              {/* Specialty Asset — Hero Strip (use-case chips). Renders only
                  for specialty assets. Reuses the existing rounded-full chip
                  pattern used elsewhere on this page. Data is derived from
                  category_specs.asset_type (no backend change). */}
              {vehicle.vehicle_category === 'specialty_asset' && (() => {
                const t = vehicle.category_specs?.asset_type;
                const sets = {
                  coffee_trailer:        ['Weddings', 'Corporate', 'Pop-ups', 'Markets', 'Private Functions'],
                  dessert_setup:         ['Weddings', 'Corporate', 'Pop-ups', 'Markets', 'Private Functions'],
                  bar_trailer:           ['Weddings', 'Corporate', 'Pop-ups', 'Private Functions'],
                  food_trailer_van:      ['Weddings', 'Corporate', 'Markets', 'Pop-ups', 'Festivals'],
                  event_trailer:         ['Weddings', 'Corporate', 'Private Functions', 'Pop-ups'],
                  event_setup:           ['Weddings', 'Corporate', 'Private Functions', 'Pop-ups'],
                  photo_booth_media:     ['Weddings', 'Corporate', 'Private Functions', 'Pop-ups'],
                  market_stall_popup:    ['Markets', 'Pop-ups', 'Festivals', 'Corporate'],
                  equipment_package:     ['Weddings', 'Corporate', 'Private Functions'],
                  furniture_package:     ['Weddings', 'Corporate', 'Private Functions'],
                  cool_room_refrigeration: ['Catering', 'Events', 'Markets', 'Festivals'],
                };
                const useCases = sets[t] || ['Weddings', 'Corporate', 'Private Functions', 'Pop-ups', 'Markets'];
                if (!useCases.length) return null;
                return (
                  <div className="mb-4 -mx-1" data-testid="specialty-hero-strip">
                    <p className="px-1 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Perfect for</p>
                    {/* Mobile: horizontal scroll. Desktop: wrap. */}
                    <div className="flex gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible px-1 pb-1 -mx-px">
                      {useCases.map((uc, idx) => (
                        <span
                          key={`uc-${idx}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-medium border border-amber-100 whitespace-nowrap flex-shrink-0"
                          data-testid={`specialty-hero-chip-${idx}`}
                        >
                          <Sparkles className="w-3 h-3 text-amber-500" />
                          {uc}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Vehicle Specs */}
              <VehicleSpecs vehicle={vehicle} />
              
              {/* Description */}
              {vehicle.description && (
                <>
                  <Separator className="my-4" />
                  <CollapsibleText 
                    text={vehicle.description}
                    previewLength={180}
                    showMoreLabel="Show full description"
                    showLessLabel="Hide description"
                  />
                </>
              )}

              {/* Specialty Asset details panel */}
              {vehicle.vehicle_category === 'specialty_asset' && (() => {
                const cs = vehicle.category_specs || {};
                const assetTypeLabel = {
                  coffee_trailer: 'Coffee / Drinks Trailer',
                  food_trailer_van: 'Food Trailer / Van',
                  dessert_setup: 'Dessert / Ice Cream Setup',
                  bar_trailer: 'Bar Trailer / Beverage Setup',
                  event_trailer: 'Event Trailer / Activation Trailer',
                  market_stall_popup: 'Market Stall / Pop-Up Setup',
                  photo_booth_media: 'Photo Booth / Media Setup',
                  equipment_package: 'Equipment Hire Package',
                  furniture_package: 'Furniture / Decor Package',
                  cool_room_refrigeration: 'Cool Room / Refrigeration Trailer',
                  event_setup: 'Event Setup',
                  other: 'Specialty Asset'
                }[cs.asset_type] || 'Specialty Asset';
                const usageLabel = {
                  pickup_by_renter: 'Pickup by renter',
                  delivered_by_host: 'Delivered by host',
                  both: 'Pickup or delivery available',
                  operator_staffed_only: 'Operator / staffed only'
                }[cs.usage_type] || null;
                const serviceModelLabel = {
                  self_hire: 'Self hire',
                  host_delivery_only: 'Host delivery only',
                  staffed_service: 'Staffed service',
                  flexible: 'Flexible'
                }[cs.service_model] || null;
                const opEnvLabel = {
                  indoor_only: 'Indoor only',
                  outdoor_only: 'Outdoor only',
                  both: 'Indoor or outdoor'
                }[cs.operating_environment] || null;
                const axleLabel = {
                  single: 'Single',
                  dual: 'Dual'
                }[cs.axle_type] || null;
                const brakeLabel = {
                  mechanical: 'Mechanical (override)',
                  electric: 'Electric',
                  hydraulic: 'Hydraulic',
                  none: 'None'
                }[cs.brake_type] || null;
                const hasDims = cs.overall_length_m || cs.overall_width_m || cs.overall_height_m;
                const isTrailerAssetType = ['coffee_trailer','food_trailer_van','dessert_setup','bar_trailer','event_trailer','cool_room_refrigeration'].includes(cs.asset_type);
                const hasTrailerSpecs = isTrailerAssetType && (hasDims || cs.tare_kg != null || axleLabel || brakeLabel);
                return (
                  <>
                    <Separator className="my-4" />
                    <div className="space-y-3 min-w-0 max-w-full" data-testid="specialty-asset-details">
                      <h3 className="font-semibold text-slate-900 text-base">Asset details</h3>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <div className="flex items-center justify-between sm:block">
                          <dt className="text-slate-500">Asset type</dt>
                          <dd className="font-medium text-slate-800">{assetTypeLabel}</dd>
                        </div>
                        {usageLabel && (
                          <div className="flex items-center justify-between sm:block">
                            <dt className="text-slate-500">Usage</dt>
                            <dd className="font-medium text-slate-800">{usageLabel}</dd>
                          </div>
                        )}
                        {serviceModelLabel && (
                          <div className="flex items-center justify-between sm:block" data-testid="specialty-service-model">
                            <dt className="text-slate-500">Service model</dt>
                            <dd className="font-medium text-slate-800">{serviceModelLabel}</dd>
                          </div>
                        )}
                        {opEnvLabel && (
                          <div className="flex items-center justify-between sm:block" data-testid="specialty-operating-environment">
                            <dt className="text-slate-500">Operating environment</dt>
                            <dd className="font-medium text-slate-800">{opEnvLabel}</dd>
                          </div>
                        )}
                        <div className="flex items-center justify-between sm:block">
                          <dt className="text-slate-500">Event / private use only</dt>
                          <dd className="font-medium text-slate-800">{cs.event_only === false ? 'No' : 'Yes'}</dd>
                        </div>
                      </dl>
                      {/* Hire inclusions trio: what's included / what renter
                          supplies / optional add-ons. Grouped section so
                          renters see all three side by side and don't miss
                          any responsibility. Each block renders only when
                          the host has filled it in. */}
                      {(cs.includes_equipment || cs.renter_must_supply || cs.addons_available) && (
                        <div className="space-y-3" data-testid="specialty-inclusions-block">
                          <h4 className="font-semibold text-slate-900 text-sm">Hire inclusions</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {cs.includes_equipment && (
                              <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3" data-testid="specialty-includes-equipment">
                                <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-1">What's included</p>
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{cs.includes_equipment}</p>
                              </div>
                            )}
                            {cs.renter_must_supply && (
                              <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3" data-testid="specialty-renter-must-supply">
                                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">You need to supply</p>
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{cs.renter_must_supply}</p>
                              </div>
                            )}
                            {cs.addons_available && (
                              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3" data-testid="specialty-addons">
                                <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-1">Optional add-ons</p>
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{cs.addons_available}</p>
                                <p className="text-xs text-slate-400 mt-1.5">Pricing/availability arranged with the host.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {cs.setup_notes && (
                        <div data-testid="specialty-setup-notes">
                          <p className="text-sm font-medium text-slate-700">Setup notes</p>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap">{cs.setup_notes}</p>
                        </div>
                      )}

                      {/* Event Timing & Delivery — specialty-only display.
                          Renders only when at least one of the new
                          category_specs fields is set. Strictly additive — no
                          impact on cars or existing specialty listings. */}
                      {(() => {
                        const hasEventTime = cs.event_start_time || cs.event_end_time;
                        const setupResp = {
                          host: 'Host handles setup & pack-down',
                          renter: 'Renter handles setup & pack-down',
                          shared: 'Shared — host delivers, renter operates'
                        }[cs.setup_responsibility];
                        const hasDelivery = cs.delivery_available || cs.pickup_available || cs.delivery_fee != null && cs.delivery_fee !== '' || cs.delivery_notes;
                        if (!hasEventTime && !setupResp && !hasDelivery) return null;
                        const fmtTime = (t) => {
                          if (!t) return null;
                          // t is "HH:MM" 24h. Render compact 12h for readability.
                          const [hh, mm] = t.split(':').map(Number);
                          if (Number.isNaN(hh)) return t;
                          const period = hh >= 12 ? 'pm' : 'am';
                          const h12 = ((hh + 11) % 12) + 1;
                          return mm ? `${h12}:${String(mm).padStart(2,'0')}${period}` : `${h12}${period}`;
                        };
                        return (
                          <div className="pt-3 mt-1 border-t border-slate-200 space-y-3" data-testid="specialty-event-delivery">
                            <h4 className="font-semibold text-slate-900 text-sm">Event timing &amp; delivery</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {hasEventTime && (
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="specialty-event-time">
                                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Operating window</p>
                                  <p className="text-sm font-medium text-slate-800">
                                    {fmtTime(cs.event_start_time) || '—'}
                                    <span className="mx-1 text-slate-400">→</span>
                                    {fmtTime(cs.event_end_time) || '—'}
                                  </p>
                                  <p className="text-[11px] text-slate-500 mt-1">Setup &amp; pack-down sit outside this window.</p>
                                </div>
                              )}
                              {setupResp && (
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="specialty-setup-responsibility">
                                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Setup &amp; pack-down</p>
                                  <p className="text-sm font-medium text-slate-800">{setupResp}</p>
                                </div>
                              )}
                            </div>
                            {hasDelivery && (
                              <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3" data-testid="specialty-delivery-structure">
                                <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2">Delivery structure</p>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {cs.delivery_available && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium px-2.5 py-0.5" data-testid="specialty-delivery-available-chip">
                                      Delivery available
                                      {cs.delivery_fee != null && cs.delivery_fee !== '' && (
                                        <span className="font-semibold"> · ${Number(cs.delivery_fee).toFixed(0)}</span>
                                      )}
                                    </span>
                                  )}
                                  {cs.pickup_available && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-800 text-xs font-medium px-2.5 py-0.5" data-testid="specialty-pickup-available-chip">
                                      Renter pickup available
                                    </span>
                                  )}
                                  {!cs.delivery_available && !cs.pickup_available && (cs.delivery_fee != null || cs.delivery_notes) && (
                                    <span className="text-xs text-slate-500">Arrange directly with host.</span>
                                  )}
                                </div>
                                {cs.delivery_notes && (
                                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{cs.delivery_notes}</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Power & Setup Requirements */}
                      {(() => {
                        const powerSourceLabel = {
                          external_power: 'External power (mains)',
                          generator: 'Generator',
                          both_supported: 'Both supported'
                        }[cs.power_source_type];
                        const inletLabel = {
                          '10A': '10A (standard)',
                          '15A': '15A',
                          'dual_15A': 'Dual 15A',
                          '32A': '32A',
                          'custom': 'Custom (see notes)'
                        }[cs.power_inlet_type];
                        const hasPower = powerSourceLabel || inletLabel || cs.number_of_power_inlets || cs.requires_separate_circuits || cs.power_requirement_summary
                          || cs.generator_supported || cs.generator_included || cs.generator_spec_notes;
                        const hasLogistics = cs.setup_time_minutes != null || cs.packdown_time_minutes != null || cs.access_requirements;
                        const hasCompliance = cs.food_service_ready || cs.requires_permits;
                        if (!hasPower && !hasLogistics && !hasCompliance) return null;
                        return (
                          <div className="pt-3 mt-1 border-t border-slate-200 space-y-3" data-testid="specialty-power-setup">
                            <h4 className="font-semibold text-slate-900 text-sm">Power &amp; Setup Requirements</h4>
                            {hasPower && (
                              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                {powerSourceLabel && (
                                  <div className="flex items-center justify-between sm:block" data-testid="power-source-type">
                                    <dt className="text-slate-500">Power source</dt>
                                    <dd className="font-medium text-slate-800">{powerSourceLabel}</dd>
                                  </div>
                                )}
                                {inletLabel && (
                                  <div className="flex items-center justify-between sm:block" data-testid="power-inlet-type">
                                    <dt className="text-slate-500">Inlet type</dt>
                                    <dd className="font-medium text-slate-800">{inletLabel}</dd>
                                  </div>
                                )}
                                {cs.number_of_power_inlets != null && cs.number_of_power_inlets !== '' && (
                                  <div className="flex items-center justify-between sm:block" data-testid="number-of-power-inlets">
                                    <dt className="text-slate-500">Power inlets</dt>
                                    <dd className="font-medium text-slate-800">{cs.number_of_power_inlets}</dd>
                                  </div>
                                )}
                                {cs.requires_separate_circuits && (
                                  <div className="flex items-center justify-between sm:block" data-testid="requires-separate-circuits">
                                    <dt className="text-slate-500">Separate circuits</dt>
                                    <dd className="font-medium text-slate-800">Required</dd>
                                  </div>
                                )}
                                {cs.generator_supported && (
                                  <div className="flex items-center justify-between sm:block" data-testid="generator-supported">
                                    <dt className="text-slate-500">Generator use</dt>
                                    <dd className="font-medium text-slate-800">Supported</dd>
                                  </div>
                                )}
                                {cs.generator_included && (
                                  <div className="flex items-center justify-between sm:block" data-testid="generator-included">
                                    <dt className="text-slate-500">Generator</dt>
                                    <dd className="font-medium text-slate-800">Included in hire</dd>
                                  </div>
                                )}
                              </dl>
                            )}
                            {cs.power_requirement_summary && (
                              <div data-testid="power-requirement-summary">
                                <p className="text-sm font-medium text-slate-700">Power requirement summary</p>
                                <p className="text-sm text-slate-600 whitespace-pre-wrap">{cs.power_requirement_summary}</p>
                              </div>
                            )}
                            {cs.generator_spec_notes && (cs.generator_supported || cs.generator_included) && (
                              <div data-testid="generator-spec-notes">
                                <p className="text-sm font-medium text-slate-700">Generator notes</p>
                                <p className="text-sm text-slate-600 whitespace-pre-wrap">{cs.generator_spec_notes}</p>
                              </div>
                            )}

                            {/* P0: Power Requirements text (host-editable, renter-visible) */}
                            {cs.power_requirements_text && (
                              <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 flex items-start gap-2" data-testid="power-requirements-text">
                                <span className="mt-0.5 inline-flex w-6 h-6 rounded-md bg-yellow-100 items-center justify-center text-yellow-700 flex-shrink-0">⚡</span>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-yellow-900 mb-0.5">Power requirements</p>
                                  <p className="text-sm text-yellow-900 whitespace-pre-wrap">{cs.power_requirements_text}</p>
                                </div>
                              </div>
                            )}

                            {/* P0: Generator clarity — concise status lines for renters */}
                            {cs.generator_included && (
                              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5 flex items-center gap-2 text-sm text-emerald-800" data-testid="generator-included-line">
                                <span>✅</span>
                                <span className="font-medium">Generator included with setup</span>
                              </div>
                            )}
                            {cs.generator_supported && !cs.generator_included && (
                              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 flex items-center gap-2 text-sm text-amber-800" data-testid="generator-not-included-line">
                                <span>⚠️</span>
                                <span className="font-medium">Generator not included — renter must supply a suitable generator</span>
                              </div>
                            )}

                            {hasLogistics && (
                              <div className="space-y-2 pt-1 border-t border-slate-100">
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                  {cs.setup_time_minutes != null && cs.setup_time_minutes !== '' && (
                                    <div className="flex items-center justify-between sm:block" data-testid="setup-time-minutes">
                                      <dt className="text-slate-500">⏱ Setup time</dt>
                                      <dd className="font-medium text-slate-800">~{cs.setup_time_minutes} minutes</dd>
                                    </div>
                                  )}
                                  {cs.packdown_time_minutes != null && cs.packdown_time_minutes !== '' && (
                                    <div className="flex items-center justify-between sm:block" data-testid="packdown-time-minutes">
                                      <dt className="text-slate-500">⏱ Pack-down time</dt>
                                      <dd className="font-medium text-slate-800">~{cs.packdown_time_minutes} minutes</dd>
                                    </div>
                                  )}
                                </dl>
                                {(cs.setup_time_minutes || cs.packdown_time_minutes) && (
                                  <p className="text-xs text-slate-500" data-testid="setup-packdown-helper">
                                    Ensure your booking duration includes setup and pack-down time.
                                  </p>
                                )}
                              </div>
                            )}
                            {cs.access_requirements && (
                              <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 flex items-start gap-2" data-testid="access-requirements">
                                <span className="mt-0.5 inline-flex w-6 h-6 rounded-md bg-slate-200 items-center justify-center text-slate-700 flex-shrink-0">🚚</span>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-700">Access requirements</p>
                                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{cs.access_requirements}</p>
                                </div>
                              </div>
                            )}

                            {hasTrailerSpecs && (
                              <div className="pt-2 border-t border-slate-100 space-y-2" data-testid="specialty-trailer-specs">
                                <h4 className="font-semibold text-slate-900 text-sm">Trailer specifications</h4>
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                  {hasDims && (
                                    <div className="flex items-center justify-between sm:block" data-testid="trailer-dimensions">
                                      <dt className="text-slate-500">Dimensions (L×W×H)</dt>
                                      <dd className="font-medium text-slate-800">
                                        {[cs.overall_length_m, cs.overall_width_m, cs.overall_height_m].map(v => v ? `${v}m` : '—').join(' × ')}
                                      </dd>
                                    </div>
                                  )}
                                  {cs.tare_kg != null && cs.tare_kg !== '' && (
                                    <div className="flex items-center justify-between sm:block" data-testid="trailer-tare">
                                      <dt className="text-slate-500">Tare</dt>
                                      <dd className="font-medium text-slate-800">{cs.tare_kg} kg</dd>
                                    </div>
                                  )}
                                  {axleLabel && (
                                    <div className="flex items-center justify-between sm:block" data-testid="trailer-axle">
                                      <dt className="text-slate-500">Axle</dt>
                                      <dd className="font-medium text-slate-800">{axleLabel}</dd>
                                    </div>
                                  )}
                                  {brakeLabel && (
                                    <div className="flex items-center justify-between sm:block" data-testid="trailer-brake">
                                      <dt className="text-slate-500">Brakes</dt>
                                      <dd className="font-medium text-slate-800">{brakeLabel}</dd>
                                    </div>
                                  )}
                                </dl>
                              </div>
                            )}

                            {hasCompliance && (
                              <div className="flex flex-wrap gap-2 pt-1" data-testid="specialty-compliance-chips">
                                {cs.food_service_ready && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium" data-testid="chip-food-service-ready">
                                    Food-service ready
                                  </span>
                                )}
                                {cs.requires_permits && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium" data-testid="chip-requires-permits">
                                    Event permits may be required
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <div className="pt-1">
                        <p className="text-sm font-medium text-slate-700">Rules for this asset</p>
                        <ul className="mt-1 space-y-1 text-sm text-slate-600 list-disc pl-5">
                          <li>No unauthorised public trading</li>
                          <li>No modification of equipment</li>
                          <li>Return in cleaned, pre-hire condition</li>
                          <li>Report any damage or malfunction immediately</li>
                        </ul>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            
            {/* Mobile Booking Card - Only visible on mobile, positioned after title */}
            <div className="lg:hidden">
              <Card className="border-0 shadow-md rounded-2xl overflow-hidden">
                <CardContent className="p-4">
                  {/* Price */}
                  <div className="flex items-baseline gap-1.5 mb-1">
                    <span className="text-2xl font-bold text-slate-900">${vehicle.daily_rate}</span>
                    <span className="text-slate-500">/day</span>
                  </div>
                  
                  {/* Social Proof: Recent Bookings */}
                  <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                    🔥 Booked {bookingCount} times this week
                  </p>
                  
                  {/* Date Selector Button */}
                  <Button 
                    onClick={() => setShowMobileCalendar(true)}
                    variant="outline"
                    className="w-full h-12 rounded-xl justify-between mb-4"
                    data-testid="select-dates-btn"
                  >
                    <span className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-slate-500" />
                      {hasStartDate() ? (
                        hasValidDateRange() ? (
                          <span className="text-slate-700">{format(selectedRange.from, "MMM d")} - {format(selectedRange.to, "MMM d")}</span>
                        ) : (
                          <span className="text-slate-700">{format(selectedRange.from, "MMM d, yyyy")}</span>
                        )
                      ) : (
                        <span className="text-slate-500">Select your dates</span>
                      )}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </Button>
                  
                  {/* Pricing Summary */}
                  {pricing && (
                    <div className="p-3 bg-slate-50 rounded-xl text-sm space-y-1.5 mb-4">
                      <div className="flex justify-between">
                        <span className="text-slate-600">${vehicle.daily_rate} × {pricing.totalDays} days</span>
                        <span>${pricing.subtotal.toFixed(2)}</span>
                      </div>
                      {isHostDirect ? (
                        <p
                          className="text-xs italic text-slate-500"
                          data-testid="no-platform-fee-note-mobile"
                        >
                          No platform fee (direct payment mode)
                        </p>
                      ) : (
                        <>
                          <div className="flex justify-between text-slate-500 text-xs">
                            <span>+ Service fee</span>
                            <span>${pricing.platformFee.toFixed(2)}</span>
                          </div>
                          {hostFeeMessage && (
                            <p className="text-xs text-emerald-600/80" data-testid="host-fee-benefit-mobile">{hostFeeMessage}</p>
                          )}
                          <div className="flex justify-between text-slate-500 text-xs">
                            <span>+ Card processing fee</span>
                            <span>${pricing.cardFee.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      <Separator className="my-1.5" />
                      <div className="flex justify-between font-semibold">
                        <span>{vehicle.vehicle_category === 'specialty_asset' ? 'Hire Total' : 'Trip Total'}</span>
                        <span>${(isHostDirect ? pricing.subtotal : pricing.tripTotal).toFixed(2)} AUD</span>
                      </div>
                      <div className="flex justify-between text-xs text-amber-700 mt-1">
                        <span>+ Bond hold (not charged)</span>
                        <span>${pricing.bondAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Trip Rules Section — category-aware: specialty assets use the
                dedicated SpecialtyRulesCard so vehicle-only host defaults
                (kms/fuel/driver age) never leak into non-vehicle listings. */}
            {vehicle.vehicle_category === 'specialty_asset' ? (
              <SpecialtyRulesCard
                rules={rules}
                vehicle={vehicle}
                data-testid="specialty-rules-section"
              />
            ) : (
              <VehicleRulesCard
                rules={rules}
                vehicle={vehicle}
                data-testid="vehicle-rules-section"
              />
            )}
            
            {/* Pickup Location Map */}
            <PickupLocationMap location={vehicle.location} />

            {/* HireHub Protection - Mobile */}
            <div className="lg:hidden">
              <HireHubProtection category={vehicle.vehicle_category} />
            </div>
            
            {/* Reviews Section */}
            <VehicleReviews vehicleId={vehicle.vehicle_id} api={api} category={vehicle.vehicle_category} />
            
            {/* Host Section */}
            <HostSection 
              host={vehicle.owner_details}
              onMessage={handleMessageHost}
            />
            
            {/* Send to a Friend Section */}
            <SendToFriendSection
              vehicleId={vehicle.vehicle_id}
              vehicleName={vehicleName}
              vehicleYear={vehicleYear}
              dailyRate={vehicle.daily_rate}
              api={api}
            />
          </div>
          
          {/* Right Column - Booking Card (Desktop Only) */}
          <div className="hidden lg:block lg:col-span-1 relative z-10">
            <div className="sticky top-24 space-y-5">
              <BookingCard 
                vehicle={vehicle}
                selectedRange={selectedRange}
                onDateRangeSelect={handleDateRangeSelect}
                pricing={pricing}
                hostFeeMessage={hostFeeMessage}
                isDateBlocked={isDateBlocked}
                onRequestToBook={handleRequestToBook}
                isExpired={isExpired}
                bookingCount={bookingCount}
                hasShownAvailabilityNudge={hasShownAvailabilityNudge}
                onShowAvailabilityNudge={() => setHasShownAvailabilityNudge(true)}
              />
              
              {/* HireHub Protection - Desktop */}
              <HireHubProtection category={vehicle.vehicle_category} />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 safe-area-inset-bottom">
        {/* Compact Trust Strip */}
        <div className="flex items-center justify-center gap-3 px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs text-slate-600">
          <span className="flex items-center gap-1">
            <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" />
            Verified Host
          </span>
          <span className="text-slate-300">•</span>
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
            {isHostDirect ? 'Direct Payment' : 'Secure Payments'}
          </span>
        </div>
        
        <div className="flex items-center gap-4 p-4">
          <div className="flex-1">
            <span className="text-xl font-bold text-slate-900">${vehicle.daily_rate}</span>
            <span className="text-slate-500 text-sm">/day</span>
            {pricing ? (
              <p className="text-xs text-slate-500">${pricing.tripTotal.toFixed(2)} {vehicle.vehicle_category === 'specialty_asset' ? 'hire' : 'trip'} + ${pricing.bondAmount.toFixed(0)} bond hold</p>
            ) : (
              <p className="text-xs text-slate-500">Select dates to see total</p>
            )}
          </div>
          <Button 
            onClick={() => {
              if (isExpired) {
                toast.error(`This ${vehicle.vehicle_category === 'specialty_asset' ? 'asset' : 'vehicle'} cannot be booked - registration expired`);
                return;
              }
              if (!hasValidDateRange()) {
                setShowMobileCalendar(true);
                return;
              }
              handleRequestToBook();
            }}
            className="bg-[#004B8D] hover:bg-[#003a6d] h-12 px-5 rounded-xl font-medium text-sm"
            disabled={isExpired}
            data-testid="mobile-book-btn"
          >
            {isExpired ? 'Unavailable' : 
              (hasValidDateRange() ? 'Request — No charge yet' : 'Select Dates')}
          </Button>
        </div>
      </div>

      {/* Mobile Calendar Modal */}
      <Dialog open={showMobileCalendar} onOpenChange={setShowMobileCalendar}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Select Dates</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Calendar
              mode="range"
              selected={selectedRange?.from ? selectedRange : undefined}
              onSelect={handleDateRangeSelect}
              disabled={(date) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const compareDate = new Date(date);
                compareDate.setHours(0, 0, 0, 0);
                return compareDate < today || isDateBlocked(date);
              }}
              className="rounded-xl border border-slate-200 mx-auto"
              numberOfMonths={1}
            />
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => handleDateRangeSelect(null)}
              className="w-full sm:w-auto"
            >
              Clear
            </Button>
            <Button 
              onClick={() => setShowMobileCalendar(false)}
              className="bg-[#004B8D] hover:bg-[#003a6d] w-full sm:w-auto"
              disabled={!hasValidDateRange()}
            >
              Confirm Dates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Confirmation Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-heading text-lg">Confirm Booking Request</DialogTitle>
          </DialogHeader>
          
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto space-y-4 pr-1"
               style={{ maxHeight: 'calc(90vh - 180px)' }}>
            {/* Vehicle Summary */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="font-semibold text-slate-900">{vehicle.make} {vehicle.model}</p>
              <p className="text-sm text-slate-500">
                {selectedRange?.from && format(selectedRange.from, 'MMM d, yyyy')} - {selectedRange?.to && format(selectedRange.to, 'MMM d, yyyy')}
              </p>
            </div>

            {/* Pricing Breakdown - Clear separation of trip cost vs bond */}
            {pricing && (
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                {/* Trip Cost - Itemised breakdown */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{vehicle.vehicle_category === 'specialty_asset' ? 'Hire Cost' : 'Trip Cost'}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">${vehicle.daily_rate} × {pricing.totalDays} days</span>
                    <span className="text-slate-900">${pricing.subtotal.toFixed(2)}</span>
                  </div>
                  {isHostDirect ? (
                    <p
                      className="text-xs italic text-slate-500"
                      data-testid="no-platform-fee-note-checkout"
                    >
                      No platform fee (direct payment mode)
                    </p>
                  ) : (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Service fee</span>
                        <span className="text-slate-600">${pricing.platformFee.toFixed(2)}</span>
                      </div>
                      {hostFeeMessage && (
                        <p className="text-xs text-emerald-600/80" data-testid="host-fee-benefit-checkout">{hostFeeMessage}</p>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Card processing fee</span>
                        <span className="text-slate-600">${pricing.cardFee.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between font-semibold text-slate-900 border-t border-slate-200 pt-2">
                    <span>{vehicle.vehicle_category === 'specialty_asset' ? 'Hire Total' : 'Trip Total'}</span>
                    <span>${(isHostDirect ? pricing.subtotal : pricing.tripTotal).toFixed(2)} AUD</span>
                  </div>
                </div>
                
                {/* Security Bond - Temporary hold */}
                <div className="flex justify-between items-start border-t border-slate-200 pt-3">
                  <div>
                    <span className="text-amber-700 font-medium">Security Bond</span>
                    <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                      {isHostDirect ? 'Collected by Host' : 'Temporary Hold'}
                    </span>
                  </div>
                  <span className="font-semibold text-amber-700">${pricing.bondAmount.toFixed(2)} AUD</span>
                </div>
                
                {/* Total Charged Today - Trip cost only */}
                <div className="flex justify-between items-start border-t-2 border-slate-300 pt-3">
                  <span className="text-slate-900 font-bold">
                    {isHostDirect ? 'Pay to Host' : 'Total Charged Today'}
                  </span>
                  <span className="font-bold text-lg text-slate-900">
                    ${(isHostDirect ? pricing.subtotal : pricing.tripTotal).toFixed(2)} AUD
                  </span>
                </div>
                
                {/* Helper text explaining the bond */}
                <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-700 leading-relaxed">
                    <strong>About the Security Bond:</strong>{' '}
                    {isHostDirect
                      ? `In direct payment mode, the security bond is collected directly by the host using the agreed method (for example PayID, bank transfer, cash, or another agreed option). HireHub records the bond for transparency but does not place a temporary hold on your card.`
                      : <>Your card will be temporarily authorised for the ${pricing.bondAmount.toFixed(0)} security bond. This hold is released after the trip if no issues occur.</>
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Trip Rules Summary — category-aware */}
            {vehicle.vehicle_category === 'specialty_asset' ? (
              <SpecialtyRulesSummary rules={rules} vehicle={vehicle} />
            ) : (
              <TripRulesSummary rules={rules} vehicle={vehicle} />
            )}

            {/* Trip Protection Badges */}
            <TripProtectionBadges />

            {/* Rules Acceptance */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl">
              <Checkbox 
                id="accept-rules" 
                checked={acceptedRules}
                onCheckedChange={setAcceptedRules}
                className="mt-0.5"
                data-testid="accept-rules-checkbox"
              />
              <label htmlFor="accept-rules" className="text-sm cursor-pointer">
                I have read and accept the host's rules and policies for this {vehicle.vehicle_category === 'specialty_asset' ? 'asset' : 'vehicle'}
              </label>
            </div>

            {/* Specialty asset — display-only reminder of what the renter
                must supply themselves. Pulled from the same
                category_specs.renter_must_supply field already shown on the
                listing. Renders only for specialty assets and only when the
                host has filled it in. No checkbox / no payload change. */}
            {vehicle.vehicle_category === 'specialty_asset' && vehicle.category_specs?.renter_must_supply && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3" data-testid="booking-modal-renter-supplies">
                <div>
                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Reminder — you'll need to supply</p>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap">{vehicle.category_specs.renter_must_supply}</p>
                </div>
                <div className="flex items-start gap-3 pt-2 border-t border-amber-200">
                  <Checkbox
                    id="confirm-supplies"
                    checked={confirmedSupplies}
                    onCheckedChange={setConfirmedSupplies}
                    className="mt-0.5"
                    data-testid="confirm-supplies-checkbox"
                  />
                  <label htmlFor="confirm-supplies" className="text-xs cursor-pointer text-slate-800">
                    I understand and confirm I will supply the required items listed above
                  </label>
                </div>
              </div>
            )}

            {/* Specialty asset — read-only context: event timing, setup
                responsibility, delivery structure. Sourced from the same
                category_specs fields shown on the listing. No checkbox / no
                payload change. Renders only for specialty assets and only
                when the host has filled at least one field in. */}
            {vehicle.vehicle_category === 'specialty_asset' && (() => {
              const cs = vehicle.category_specs || {};
              const hasEventTime = cs.event_start_time || cs.event_end_time;
              const setupRespLabel = {
                host: 'Host handles setup & pack-down',
                renter: 'Renter handles setup & pack-down',
                shared: 'Shared — host delivers, renter operates'
              }[cs.setup_responsibility];
              const hasDelivery = cs.delivery_available || cs.pickup_available || (cs.delivery_fee != null && cs.delivery_fee !== '') || cs.delivery_notes;
              if (!hasEventTime && !setupRespLabel && !hasDelivery) return null;
              const fmtTime = (t) => {
                if (!t) return null;
                const [hh, mm] = String(t).split(':').map(Number);
                if (Number.isNaN(hh)) return t;
                const period = hh >= 12 ? 'pm' : 'am';
                const h12 = ((hh + 11) % 12) + 1;
                return mm ? `${h12}:${String(mm).padStart(2,'0')}${period}` : `${h12}${period}`;
              };
              return (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2" data-testid="booking-modal-event-delivery">
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Event &amp; delivery details</p>
                  <dl className="space-y-1.5 text-xs text-slate-700">
                    {hasEventTime && (
                      <div className="flex items-start justify-between gap-3" data-testid="booking-modal-event-time">
                        <dt className="text-slate-500 shrink-0">Operating window</dt>
                        <dd className="font-medium text-slate-800 text-right">
                          {fmtTime(cs.event_start_time) || '—'}
                          <span className="mx-1 text-slate-400">→</span>
                          {fmtTime(cs.event_end_time) || '—'}
                        </dd>
                      </div>
                    )}
                    {setupRespLabel && (
                      <div className="flex items-start justify-between gap-3" data-testid="booking-modal-setup-responsibility">
                        <dt className="text-slate-500 shrink-0">Setup &amp; pack-down</dt>
                        <dd className="font-medium text-slate-800 text-right">{setupRespLabel}</dd>
                      </div>
                    )}
                    {hasDelivery && (
                      <div className="pt-1.5 border-t border-slate-200 space-y-1.5" data-testid="booking-modal-delivery-structure">
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-slate-500 shrink-0">Delivery</dt>
                          <dd className="font-medium text-slate-800 text-right">
                            {[
                              cs.delivery_available ? `Delivery${cs.delivery_fee != null && cs.delivery_fee !== '' ? ` ($${Number(cs.delivery_fee).toFixed(0)})` : ''}` : null,
                              cs.pickup_available ? 'Renter pickup' : null
                            ].filter(Boolean).join(' · ') || 'Arrange with host'}
                          </dd>
                        </div>
                        {cs.delivery_notes && (
                          <p className="text-[11px] text-slate-600 whitespace-pre-wrap">{cs.delivery_notes}</p>
                        )}
                      </div>
                    )}
                  </dl>
                </div>
              );
            })()}

            {/* P0 LICENCE VALIDITY: Driver / Operator Declaration */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-800">{vehicle.vehicle_category === 'specialty_asset' ? 'Operator Declaration' : 'Driver Licence Declaration'}</span>
              </div>
              
              {licenceValidity?.requires_reverification && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
                  <p className="text-sm text-red-700 font-medium">Licence Re-Verification Required</p>
                  <p className="text-xs text-red-600 mt-1">
                    {licenceValidity?.reasons?.join('; ') || 'Please update your verification documents.'}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 border-red-300 text-red-700 hover:bg-red-100"
                    onClick={() => navigate('/verification')}
                    data-testid="update-verification-btn"
                  >
                    Update Verification
                  </Button>
                </div>
              )}
              
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Checkbox 
                    id="licence-valid" 
                    checked={licenceDeclaration.declared_licence_valid}
                    onCheckedChange={(checked) => setLicenceDeclaration(prev => ({...prev, declared_licence_valid: checked}))}
                    className="mt-0.5"
                    data-testid="licence-valid-checkbox"
                  />
                  <label htmlFor="licence-valid" className="text-xs text-slate-700 cursor-pointer">
                    {vehicle.vehicle_category === 'specialty_asset'
                      ? 'I confirm I hold any licence/permit required to operate this asset (if applicable)'
                      : 'I confirm my driver licence is current and valid'}
                  </label>
                </div>
                
                <div className="flex items-start gap-2">
                  <Checkbox 
                    id="licence-not-suspended" 
                    checked={licenceDeclaration.declared_not_suspended}
                    onCheckedChange={(checked) => setLicenceDeclaration(prev => ({...prev, declared_not_suspended: checked}))}
                    className="mt-0.5"
                    data-testid="licence-not-suspended-checkbox"
                  />
                  <label htmlFor="licence-not-suspended" className="text-xs text-slate-700 cursor-pointer">
                    {vehicle.vehicle_category === 'specialty_asset'
                      ? 'I confirm I am not prohibited from operating this type of asset'
                      : 'I confirm my licence has not been suspended, cancelled, or disqualified'}
                  </label>
                </div>
                
                <div className="flex items-start gap-2">
                  <Checkbox 
                    id="licence-legally-permitted" 
                    checked={licenceDeclaration.declared_legally_permitted}
                    onCheckedChange={(checked) => setLicenceDeclaration(prev => ({...prev, declared_legally_permitted: checked}))}
                    className="mt-0.5"
                    data-testid="licence-legally-permitted-checkbox"
                  />
                  <label htmlFor="licence-legally-permitted" className="text-xs text-slate-700 cursor-pointer">
                    {vehicle.vehicle_category === 'specialty_asset'
                      ? 'I confirm I am legally permitted to operate this asset'
                      : 'I confirm I am legally permitted to drive this vehicle'}
                  </label>
                </div>
                
                <div className="flex items-start gap-2">
                  <Checkbox 
                    id="licence-belongs-to-me" 
                    checked={licenceDeclaration.declared_licence_belongs_to_me}
                    onCheckedChange={(checked) => setLicenceDeclaration(prev => ({...prev, declared_licence_belongs_to_me: checked}))}
                    className="mt-0.5"
                    data-testid="licence-belongs-to-me-checkbox"
                  />
                  <label htmlFor="licence-belongs-to-me" className="text-xs text-slate-700 cursor-pointer">
                    {vehicle.vehicle_category === 'specialty_asset'
                      ? 'I confirm any verification documents I uploaded belong to me'
                      : 'I confirm the licence I uploaded belongs to me'}
                  </label>
                </div>
              </div>
            </div>

            {/* PHASE 2 (P0/P1): Category-specific operator declarations.
                Rendered only for categories defined in CATEGORY_DECLARATIONS.
                Strictly additive — does not affect cars or specialty assets. */}
            {(CATEGORY_DECLARATIONS[vehicle?.vehicle_category] || []).length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid="category-declarations-block">
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
                  Operator responsibilities
                </p>
                <div className="space-y-2">
                  {(CATEGORY_DECLARATIONS[vehicle.vehicle_category] || []).map((decl) => (
                    <div key={decl.key} className="flex items-start gap-2">
                      <Checkbox
                        id={`category-decl-${decl.key}`}
                        checked={!!categoryDeclarations[decl.key]}
                        onCheckedChange={(checked) =>
                          setCategoryDeclarations((prev) => ({ ...prev, [decl.key]: !!checked }))
                        }
                        className="mt-0.5"
                        data-testid={`category-decl-${decl.key}-checkbox`}
                      />
                      <label
                        htmlFor={`category-decl-${decl.key}`}
                        className="text-xs text-slate-700 cursor-pointer"
                      >
                        {decl.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legal Acceptance Warning */}
            {userNeedsLegalAcceptance && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">Terms & Privacy Acceptance Required</p>
                  <p className="text-xs text-red-600 mt-1">You must accept our updated Terms of Service and Privacy Policy before booking.</p>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="mt-2 border-red-300 text-red-700 hover:bg-red-100"
                    onClick={() => {
                      setShowBookingModal(false);
                      setShowLegalModal(true);
                    }}
                    data-testid="accept-terms-modal-btn"
                  >
                    Accept Terms & Privacy
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 flex-shrink-0 pt-4 border-t border-slate-100">
            <Button variant="outline" onClick={() => setShowBookingModal(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              onClick={handleBookingRequest}
              disabled={!acceptedRules || bookingLoading || licenceValidity?.requires_reverification || 
                !licenceDeclaration.declared_licence_valid || 
                !licenceDeclaration.declared_not_suspended || 
                !licenceDeclaration.declared_legally_permitted || 
                !licenceDeclaration.declared_licence_belongs_to_me ||
                (vehicle?.vehicle_category === 'specialty_asset' && vehicle?.category_specs?.renter_must_supply && !confirmedSupplies) ||
                // PHASE 2: Block when any required category declaration unchecked
                ((CATEGORY_DECLARATIONS[vehicle?.vehicle_category] || []).some((d) => !categoryDeclarations[d.key]))}
              className="bg-[#004B8D] hover:bg-[#003a6d] w-full sm:w-auto"
              data-testid="confirm-booking-btn"
            >
              {bookingLoading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Host Modal */}
      <HostModal 
        host={vehicle?.owner_details}
        isOpen={showHostModal}
        onClose={() => setShowHostModal(false)}
        onMessage={handleMessageHost}
        api={api}
      />

      {/* Verification Gate Modal */}
      <VerificationGateModal 
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        verificationStatus={user?.verification_status}
        returnUrl={`/vehicle/${vehicleId}`}
      />

      {/* Legal Acceptance Modal */}
      <LegalReacceptanceModal 
        isOpen={showLegalModal}
        onAccepted={handleLegalAccepted}
      />

      {/* Guest Checkout Modal */}
      <GuestCheckoutModal
        isOpen={showGuestCheckoutModal}
        onClose={() => setShowGuestCheckoutModal(false)}
        vehicleId={vehicleId}
        vehicleName={vehicleName}
        selectedDates={selectedRange}
        onGuestAccountCreated={(data) => {
          // Guest account created - they'll be redirected to verification
          toast.success('Account created! Check your email to set your password.');
          setShowGuestCheckoutModal(false);
        }}
      />

      {/* Header Share Modal - Reuses SendToFriend component */}
      <SendToFriendModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareLinks={shareLinks}
        vehicleName={vehicleName}
        vehicleYear={vehicleYear}
        dailyRate={vehicle.daily_rate}
      />
    </div>
  );
};

export default VehicleDetails;
